/**
 * CTS TIMING POINTS AS PILLARS, drawn into the future the price bars have not reached.
 *
 * <h2>Why these may be drawn ahead of the playhead when a trade marker may not</h2>
 *
 * The replay hides everything after the playhead, and the rule is load-bearing: an exit drawn
 * early leaks the answer the replay exists to withhold. A CTS date is the exception for the same
 * reason a PTA/PTB generation band is — it is PURE CALENDAR. The geometry that produces it is
 * astronomical and fixed centuries ahead, so a pillar at a future date reveals nothing the engine
 * did not already know at bar one. What it cannot say is what price will do there, and it does not
 * try to.
 *
 * <p>That is the whole value of the forward half: the reader sees the timing point standing in
 * empty space and watches the bars animate INTO it. A pillar that appeared only once price arrived
 * would be a mark on a bar rather than a prediction about one.
 *
 * <h2>The marks come from the scan that has already been run — not from a second source</h2>
 *
 * The points handed in are the ones the Critical Timing Scan drew, aligned by `reversalBars`
 * against the EXTENDED axis. So the dataset is read once, the fold and the bar alignment happen
 * once, and the replay cannot disagree with the violet triangles already on the chart about which
 * days are timing points. Loading the dataset again here would be a second alignment of the same
 * calendar, which is how two views of one study come to show different days.
 *
 * <h2>NON-TRADING DAYS keep the established convention</h2>
 *
 * A timing point frequently lands on a weekend or a holiday, and a non-trading date is not a
 * category on the price axis at all — so it is snapped forward to the next bar. The codebase
 * already has a convention for saying so, from the celestial study: drawn HIGHER, and HOLLOW.
 * Both are reproduced here, with the same two distances, so a reader who has learned it on one
 * study reads it correctly on the other. `ctsPillars.test.js` pins the numbers against that
 * study's own exported constants, because a convention held in two places by coincidence is not a
 * convention.
 */

import { CTS_COLOR, CTS_MARK_RIM } from './simulatorPalette';
import { ctsMarkStyle, CTS_RAIL_COLOR } from './ctsMarkStyle';
import { RAIL_Y_POSITION } from './chartRail';

/**
 * Royal purple, from the palette — the one place the simulator's colours are declared.
 *
 * <p>It was `#b388ff`, which was violet enough but collided with PTB's old violet. The two are
 * now a hue apart by construction rather than by luck.
 */
export const CTS_PILLAR_COLOR = CTS_COLOR;

/** Clear of the candle. The same distance the celestial study uses for a mark on its own day. */
export const CTS_PILLAR_OFFSET_PX = 14;

/** ...and clear of THOSE for one snapped forward from a non-trading day. */
export const CTS_SNAPPED_OFFSET_PX = 34;

export const CTS_PILLAR_SERIES_ID = 'dynaman-cts-pillars-series';
/** The second series, on the PRICE axis, carrying the marks whose bars are revealed. */
export const CTS_PILLAR_PRICE_SERIES_ID = 'dynaman-cts-pillars-price-series';
export const CTS_PILLAR_OVERLAY_ID = 'dynaman-cts-pillars';

/**
 * Faint, because there can be hundreds of them and they are context rather than a finding.
 *
 * <p>At full strength a few hundred full-height verticals is a hatched screen and the candles stop
 * being readable — which would defeat the one thing a visual audit needs.
 */
export const PILLAR_OPACITY = 0.3;

/**
 * How many pillars are drawn.
 *
 * <p>ECharts lays out every `markLine` datum on every redraw, and the replay redraws on every
 * tick. A 300-year CTS dataset marks thousands of sessions; at a few hundred verticals the
 * step-through stops being smooth, which is the one thing it cannot afford.
 */
export const MAX_PILLARS = 160;

const asPoint = (p) => {
  if (!p) return null;
  if (typeof p === 'string') return { date: p, price: null, firedOn: null, up: null };
  if (!p.date) return null;
  return {
    date: p.date,
    price: Number.isFinite(p.price) ? p.price : null,
    // The direction, when the caller knows it. NULL rather than a guess: a mark whose signal
    // cannot be read must fall back to the neutral fill, not to an arbitrary half of the pair.
    up: typeof p.up === 'boolean' ? p.up : null,
    // The day the scan actually fired, which for a snapped mark is not the bar it is drawn on.
    // `title` is what the existing CTS overlay calls it, and it may list several days that landed
    // on one bar.
    firedOn: p.firedOn || p.title || null,
  };
};

/** A point is SNAPPED when the day it fired is not the bar it is drawn on. */
export function isSnapped(point) {
  if (!point || !point.firedOn) return false;
  // A bar several days may have snapped onto carries them all; the mark is snapped unless the
  // bar's own date is among them.
  return !String(point.firedOn).split(',').map((d) => d.trim()).includes(point.date);
}

/**
 * The verticals, every one of them — past, present and future.
 *
 * <p>Centred on the pillars NEAREST THE PLAYHEAD when the cap bites, not the first ones found. A
 * reader stepping through a trade cares about the timing points around where they are, and
 * truncating from either end alone would drop exactly those.
 */
export function pillarLines(points, dates, upToDate) {
  const axis = Array.isArray(dates) ? dates : [];
  if (axis.length === 0) return [];

  const usable = (Array.isArray(points) ? points : [])
    .map(asPoint)
    .filter((p) => p && axis.indexOf(p.date) >= 0);

  const chosen = nearest(usable, upToDate, MAX_PILLARS);

  return chosen.map((p) => {
    const snapped = isSnapped(p);
    return {
      xAxis: p.date,
      lineStyle: {
        color: CTS_PILLAR_COLOR,
        width: 1,
        // DASHED when snapped. A vertical has no height channel to offset, so the silhouette has
        // to carry it — the same answer the celestial study's own rail reached.
        type: snapped ? 'dashed' : 'dotted',
        opacity: PILLAR_OPACITY,
      },
      label: { show: false },
    };
  });
}

/**
 * The marks AT THE FOOT OF THE GRID, where the non-trading-day convention is visible.
 *
 * <p>⚠ These used to sit at the PRICE the caller supplied, and on this overlay that was the worst
 * place for them: the pillars are drawn ahead of the playhead on purpose — a CTS date is pure
 * calendar and reveals nothing — but a mark at a price is a mark on a bar the reader has not been
 * shown. Moving them to the rail keeps the forward half, which is the whole point of the overlay,
 * and retracts the one claim it was never entitled to make.
 *
 * <p>So no price is needed and none is read. The vertical says WHEN; this says whether the day
 * was a trading day, by sitting higher and drawing hollow.
 */
export function pillarMarks(points, dates, upToDate) {
  const axis = Array.isArray(dates) ? dates : [];
  if (axis.length === 0) return [];

  const usable = (Array.isArray(points) ? points : [])
    .map(asPoint)
    .filter((p) => p && axis.indexOf(p.date) >= 0);

  return nearest(usable, upToDate, MAX_PILLARS).map((p) => {
    const snapped = isSnapped(p);
    /*
     * AT THE PRICE WHERE THERE IS ONE, at the foot of the grid where there is not.
     *
     * <p>These went wholly to the rail and that over-corrected: a mark on a bar the reader can
     * see has a level to sit at, and burying it threw that away for the revealed half of every
     * replay. The forward half is the half that needed moving — a pillar is drawn AHEAD of the
     * playhead on purpose, being pure calendar, and a mark at a price there is a mark on a bar
     * nobody has seen.
     *
     * <p>So it is per point and it MOVES: as the clock advances, each mark snaps off the rail
     * onto the chart on the bar the playhead reaches it. Two ways a bar has no price here — the
     * caller gave none, or the date is past the playhead.
     */
    const onRail = p.price === null || (upToDate && p.date > upToDate);
    return {
      // ⚠ A markPoint reads its `coord` against the series' OWN axis, and this series is on the
      // rail — so a revealed mark cannot simply carry a price here. `ctsPillarMarks` emits the
      // two halves as separate payloads for that reason; see its note.
      coord: [p.date, onRail ? RAIL_Y_POSITION : p.price],
      symbol: snapped ? 'emptyTriangle' : 'triangle',
      symbolSize: 8,
      // ROTATED for a down signal, but only once the mark is at its price. On the rail it must
      // stay upright: half the triangles pointing down is a directional claim, and the rail
      // position exists to retract exactly that.
      ...(!onRail && p.up === false ? { symbolRotate: 180 } : {}),
      // DRAWN HIGHER when snapped, which is the established convention stated as a distance, and
      // it still reads at the foot of the grid: further up from the floor. The two distances are
      // unchanged, so a reader who learned the convention on the celestial study reads it here.
      symbolOffset: [0, -(snapped ? CTS_SNAPPED_OFFSET_PX : CTS_PILLAR_OFFSET_PX)],
      // Which half this belongs to, read by `ctsPillarMarks` and stripped before it reaches
      // ECharts. A stray key on a markPoint datum is ignored rather than rejected, but leaving
      // one would make the payload say something ECharts does not read.
      onRail,
      /*
       * THE SAME THREE-STATE FILL the scan's own triangles use, from the same declaration, so
       * the two ways of drawing a CTS point cannot disagree about what one looks like: green or
       * red at a price, neutral amber on the rail.
       *
       * <p>A SNAPPED mark keeps its hollow convention and takes the colour on its BORDER, because
       * that is where its colour already lived -- the fill is what says "this fired on a
       * non-trading day", and spending it on the direction would cost a reading to add one.
       *
       * <p>The full-height PILLAR keeps CTS purple throughout: it is the time WINDOW rather than
       * the point, and the two being different colours is what tells them apart at the foot of
       * the grid.
       */
      itemStyle: (() => {
        const fill = onRail || typeof p.up !== 'boolean'
          ? CTS_RAIL_COLOR
          : ctsMarkStyle(p.up).color;
        return snapped
          ? { color: 'transparent', borderColor: fill, borderWidth: 1.5, opacity: 0.9 }
          : { color: fill, borderColor: CTS_MARK_RIM, borderWidth: 1, opacity: 0.9 };
      })(),
      label: { show: false },
      name: snapped
        ? `CTS ${p.firedOn} — a non-trading day, drawn on ${p.date}`
        : `CTS ${p.date}`,
    };
  });
}

/** The `count` points closest in date to `at`, in axis order. */
function nearest(points, at, count) {
  if (points.length <= count) return points;
  if (!at) return points.slice(-count);
  const sorted = [...points].sort((a, b) => keyDistance(a.date, at) - keyDistance(b.date, at));
  return sorted.slice(0, count).sort((a, b) => (a.date < b.date ? -1 : 1));
}

/** Distance in days between two ISO dates, parsed as UTC so no reader's zone can shift one. */
function keyDistance(a, b) {
  const x = Date.parse(`${String(a).slice(0, 10)}T00:00:00Z`);
  const y = Date.parse(`${String(b).slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(x) || Number.isNaN(y)) return Number.MAX_SAFE_INTEGER;
  return Math.abs(x - y);
}

/**
 * The whole pillar series, as one payload.
 *
 * <p>Both marks declared even when empty: a merge cannot REMOVE one by omitting it, so a replay
 * that switched symbol would otherwise keep the previous instrument's timing points frozen over
 * the new chart.
 */
export function ctsPillarMarks(points, dates, upToDate) {
  const marks = pillarMarks(points, dates, upToDate);
  /*
   * TWO PAYLOADS, because a markPoint's `coord` is read against its series' OWN y axis and the
   * two halves are on different ones: a masked mark sits at 0.03 on the pinned 0..1 rail and a
   * revealed one sits at a price. One series cannot carry both -- a price of 450 handed to the
   * rail axis lands somewhere off the top of the world.
   *
   * <p>The VERTICALS ride the rail payload, and nothing about them changes: a markLine datum of
   * `{xAxis: date}` spans the grid whatever the y axis is, which is what lets a pillar stand in
   * empty space ahead of the playhead while the mark at its foot waits for a price.
   *
   * <p>Both are returned even when empty. The replay merges these by series id on every bar, and
   * a merge cannot REMOVE a mark by omitting it -- so a payload that disappeared on the bar its
   * last mark moved to the other axis would leave that mark frozen on screen.
   */
  // `onRail` is the partition key and must not reach ECharts, which is the whole of this.
  const strip = (m) => { const out = { ...m }; delete out.onRail; return out; };
  return {
    rail: {
      markLine: {
        silent: true,
        symbol: 'none',
        animation: false,
        data: pillarLines(points, dates, upToDate),
      },
      markPoint: {
        silent: true,
        animation: false,
        data: marks.filter(m => m.onRail).map(strip),
      },
    },
    price: {
      markPoint: {
        silent: true,
        animation: false,
        data: marks.filter(m => !m.onRail).map(strip),
      },
    },
  };
}
